import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { PageHeader } from "../../components/app/Common";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../../components/ui/dialog";
import { ChevronLeft, ChevronRight, Copy, Loader2, Plus, RefreshCw, Search, Trash2 } from "lucide-react";

/* ---------- datas e horas (tudo no horario do aparelho) ---------- */

const pad = (n) => String(n).padStart(2, "0");
const dayKey = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const timeValue = (d) => `${pad(d.getHours())}:${pad(d.getMinutes())}`;
const addDays = (d, n) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n, d.getHours(), d.getMinutes());
const parseLocal = (date, time) => new Date(`${date}T${time}:00`);
const hoursBetween = (a, b) => Math.max(0, (b - a) / 3600000);

function startOfWeek(d) {
  const x = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  x.setDate(x.getDate() - ((x.getDay() + 6) % 7)); // semana comeca na segunda
  return x;
}

function clock(d) {
  const h = d.getHours();
  return `${h % 12 || 12}:${pad(d.getMinutes())}${h >= 12 ? "pm" : "am"}`;
}

const fmtH = (h) => `${Math.round(h * 10) / 10}h`;
const fmtMoney = (v) => `$${Math.round(v).toLocaleString("en-AU")}`;
const shortDate = (d) => d.toLocaleDateString("en-AU", { day: "numeric", month: "short" });
const weekdayName = (d) => d.toLocaleDateString("en-AU", { weekday: "short" });

// O servidor devolve os turnos por data de inicio; pedimos um dia a mais de cada lado para nao perder nada
// por causa de fuso horario, e depois filtramos certinho aqui.
const rangeParams = (ws) => ({ date_from: addDays(ws, -1).toISOString(), date_to: addDays(ws, 8).toISOString() });

const shiftHours = (s) => hoursBetween(new Date(s.start_time), new Date(s.end_time));
const shiftCost = (s) => shiftHours(s) * (Number(s.hourly_rate) || 0);

// Quem esta no turno: confirmados e quem pediu para sair (continua nele ate o pedido ser aprovado).
// "claims" sao pedidos de pessoas que querem pegar um turno aberto.
function assignmentOf(s) {
  const allocs = s.allocations || [];
  const confirmed = allocs.filter((a) => a.status === "confirmed");
  const drops = allocs.filter((a) => a.status === "pending_approval" && a.request_type === "drop");
  const claims = allocs.filter((a) => a.status === "pending_approval" && a.request_type !== "drop");
  return { confirmed, drops, claims, assigned: [...confirmed, ...drops] };
}

const LAST_KEY = "ss_roster_last";
function readLast() {
  try {
    return JSON.parse(localStorage.getItem(LAST_KEY) || "{}") || {};
  } catch (e) {
    return {};
  }
}
function saveLast(form) {
  try {
    localStorage.setItem(
      LAST_KEY,
      JSON.stringify({ start: form.start, end: form.end, title: form.title, siteId: form.siteId, rate: form.rate })
    );
  } catch (e) {
    // sem armazenamento: so nao lembra
  }
}

/* ---------- pecas pequenas ---------- */

const apiBase = () => (api.defaults?.baseURL || "").replace(/\/api\/?$/, "");
function avatarSrc(u) {
  const a = u?.avatar_url;
  if (!a) return "";
  return /^https?:/i.test(a) ? a : `${apiBase()}${a}`;
}

function Avatar({ user, size = 36 }) {
  const [broken, setBroken] = useState(false);
  const src = avatarSrc(user);
  const initials = (user?.name || "?")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join("");
  const box = { width: size, height: size };
  if (src && !broken) {
    return <img src={src} alt="" onError={() => setBroken(true)} style={box} className="shrink-0 rounded-full object-cover" />;
  }
  return (
    <div style={box} className="grid shrink-0 place-items-center rounded-full bg-primary/15 text-xs font-bold text-primary">
      {initials}
    </div>
  );
}

function ShiftCard({ shift, siteName, onOpen, onDragStart, onDragEnd }) {
  const s = new Date(shift.start_time);
  const e = new Date(shift.end_time);
  const { assigned, claims, drops } = assignmentOf(shift);
  const open = assigned.length === 0;
  const activate = () => onOpen(shift);
  return (
    <div
      role="button"
      tabIndex={0}
      draggable
      onDragStart={(ev) => onDragStart(ev, shift)}
      onDragEnd={onDragEnd}
      onClick={activate}
      onKeyDown={(ev) => {
        if (ev.key === "Enter" || ev.key === " ") {
          ev.preventDefault();
          activate();
        }
      }}
      className={`w-full cursor-pointer select-none rounded-xl border px-2.5 py-2 text-left transition-colors md:rounded-lg ${
        open
          ? "border-dashed border-amber-500/60 bg-amber-500/5 hover:border-amber-500"
          : "border-border border-l-4 border-l-primary bg-background hover:border-primary/60"
      }`}
      data-testid={`shift-${shift.id}`}
    >
      <div className="text-[12px] font-bold leading-tight">
        {clock(s)} – {clock(e)}
      </div>
      <div className="mt-0.5 truncate text-[12px] leading-tight">{shift.title}</div>
      {siteName ? <div className="truncate text-[11px] leading-tight text-muted-foreground">{siteName}</div> : null}
      {claims.length > 0 && (
        <div className="mt-1 inline-block rounded-full bg-amber-500/15 px-2 py-0.5 text-[10px] font-semibold text-amber-500" data-testid={`claims-${shift.id}`}>
          {claims.length} request{claims.length === 1 ? "" : "s"}
        </div>
      )}
      {drops.length > 0 && (
        <div className="mt-1 inline-block rounded-full bg-red-500/15 px-2 py-0.5 text-[10px] font-semibold text-red-400">Wants to drop</div>
      )}
    </div>
  );
}

function Cell({ id, items, today, label, siteNameOf, dragOver, setDragOver, onAdd, onOpen, onDragStart, onDragEnd, onDropHere }) {
  return (
    <div
      className={`group relative min-h-[88px] space-y-1.5 border-b border-l border-border p-1.5 ${today ? "bg-primary/[0.04]" : ""} ${
        dragOver === id ? "bg-primary/10 ring-2 ring-inset ring-primary/60" : ""
      }`}
      onDragOver={(ev) => {
        ev.preventDefault();
        if (dragOver !== id) setDragOver(id);
      }}
      onDrop={(ev) => {
        ev.preventDefault();
        setDragOver("");
        onDropHere(ev);
      }}
      data-testid={`cell-${id}`}
    >
      {items.map((s) => (
        <ShiftCard key={s.id} shift={s} siteName={siteNameOf(s)} onOpen={onOpen} onDragStart={onDragStart} onDragEnd={onDragEnd} />
      ))}
      {items.length === 0 ? (
        <button
          type="button"
          onClick={onAdd}
          aria-label={label}
          className="grid h-full min-h-[72px] w-full place-items-center rounded-lg text-muted-foreground/40 transition-colors hover:bg-accent/50 hover:text-primary active:bg-accent"
          data-testid={`add-${id}`}
        >
          <Plus className="h-4 w-4" />
        </button>
      ) : (
        <button
          type="button"
          onClick={onAdd}
          aria-label={label}
          className="hidden w-full place-items-center rounded-md py-1 text-muted-foreground/50 hover:text-primary group-hover:grid"
          data-testid={`add-more-${id}`}
        >
          <Plus className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}

function Chip({ children, tone }) {
  const cls =
    tone === "warn"
      ? "border-amber-500/40 bg-amber-500/10 text-amber-500"
      : "border-border bg-card text-muted-foreground";
  return <span className={`rounded-full border px-3 py-1 text-xs font-medium ${cls}`}>{children}</span>;
}

/* ---------- janela de criar / editar turno ---------- */

function buildForm(state, users, siteFilter) {
  const last = readLast();
  if (state.shift) {
    const s = state.shift;
    const sd = new Date(s.start_time);
    const ed = new Date(s.end_time);
    const who = assignmentOf(s).assigned[0];
    return {
      id: s.id,
      userId: who ? who.user_id : "",
      date: dayKey(sd),
      start: timeValue(sd),
      end: timeValue(ed),
      title: s.title || "",
      siteId: s.jobsite_id || "",
      rate: s.hourly_rate ? String(s.hourly_rate) : "",
      notes: s.notes || "",
      rateTouched: true,
    };
  }
  const person = users.find((u) => u.id === state.userId);
  const personRate = person && Number(person.hourly_rate) > 0 ? String(person.hourly_rate) : "";
  return {
    id: null,
    userId: state.userId || "",
    date: dayKey(state.day),
    start: last.start || "07:00",
    end: last.end || "15:30",
    title: last.title || "Shift",
    siteId: siteFilter || last.siteId || "",
    rate: personRate || last.rate || "",
    notes: "",
    rateTouched: false,
  };
}

const fieldClass = "h-11 rounded-lg text-base md:h-9 md:rounded-sm md:text-sm";
const selectClass =
  "h-11 w-full rounded-lg border border-border bg-background px-3 text-base text-foreground focus:outline-none focus:ring-1 focus:ring-primary md:h-9 md:rounded-sm md:text-sm";

function ShiftSheet({ state, users, sites, shifts, titles, siteFilter, onClose, onChanged }) {
  const editing = !!state.shift;
  const [form, setForm] = useState(() => buildForm(state, users, siteFilter));
  const [busy, setBusy] = useState("");
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // sempre o turno "vivo" da lista (assim os pedidos aparecem atualizados)
  const live = editing ? shifts.find((x) => x.id === form.id) || state.shift : null;
  const { claims, drops } = live ? assignmentOf(live) : { claims: [], drops: [] };

  const start = parseLocal(form.date, form.start);
  const end = parseLocal(form.date, form.end);
  const validTimes = !Number.isNaN(start.getTime()) && !Number.isNaN(end.getTime()) && end > start;
  const duration = validTimes ? hoursBetween(start, end) : 0;

  const conflict = useMemo(() => {
    if (!form.userId || !validTimes) return null;
    return (
      shifts.find(
        (o) =>
          o.id !== form.id &&
          assignmentOf(o).assigned.some((a) => a.user_id === form.userId) &&
          new Date(o.start_time) < end &&
          new Date(o.end_time) > start
      ) || null
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.userId, form.date, form.start, form.end, form.id, shifts]);

  const canSave = !busy && form.title.trim() && validTimes && !conflict;

  const choosePerson = (id) => {
    setForm((f) => {
      const u = users.find((x) => x.id === id);
      const r = !f.rateTouched && u && Number(u.hourly_rate) > 0 ? String(u.hourly_rate) : f.rate;
      return { ...f, userId: id, rate: r };
    });
  };

  const save = async () => {
    if (!canSave) return;
    const body = {
      title: form.title.trim(),
      start_time: start.toISOString(),
      end_time: end.toISOString(),
      hourly_rate: Number(form.rate) || 0,
      jobsite_id: form.siteId || null,
      notes: form.notes || "",
    };
    setBusy("save");
    try {
      if (editing) {
        await api.post(`/shifts/${form.id}/move`, form.userId ? { ...body, user_id: form.userId } : { ...body, unassign: true });
        toast.success("Shift updated");
      } else {
        const { data: created } = await api.post("/shifts", { ...body, role_required: "worker" });
        if (form.userId) {
          try {
            await api.post(`/shifts/${created.id}/allocate`, { user_id: form.userId });
          } catch (e) {
            await api.delete(`/shifts/${created.id}`).catch(() => {});
            throw e;
          }
        }
        toast.success(form.userId ? "Shift added" : "Open shift added");
      }
      saveLast(form);
      onChanged();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, "Could not save the shift."));
      setBusy("");
    }
  };

  const makeOpen = async () => {
    setBusy("open");
    try {
      await api.post(`/shifts/${form.id}/move`, { unassign: true });
      toast.success("Shift is now open");
      onChanged();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, "Could not update the shift."));
      setBusy("");
    }
  };

  const remove = async () => {
    if (!confirm("Delete this shift? This cannot be undone.")) return;
    setBusy("delete");
    try {
      await api.delete(`/shifts/${form.id}`);
      toast.success("Shift deleted");
      onChanged();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, "Could not delete the shift."));
      setBusy("");
    }
  };

  const review = async (alloc, status) => {
    setBusy("review");
    try {
      await api.patch(`/allocations/${alloc.id}`, { status });
      toast.success(status === "confirmed" ? "Approved" : "Declined");
      onChanged();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, "Could not update the request."));
      setBusy("");
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="max-h-[92vh] w-[calc(100%-1.5rem)] max-w-md overflow-y-auto rounded-xl md:rounded-sm" data-testid="shift-sheet">
        <DialogHeader>
          <DialogTitle>{editing ? "Edit shift" : "Add shift"}</DialogTitle>
          <DialogDescription>{editing ? "Change the hours, the person or the details." : "Choose the person and the hours."}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label className="mb-1 block text-sm font-medium md:text-xs">Person</Label>
            <select value={form.userId} onChange={(e) => choosePerson(e.target.value)} className={selectClass} data-testid="f-person">
              <option value="">Open shift (nobody yet)</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <Label className="mb-1 block text-sm font-medium md:text-xs">Date</Label>
            <Input type="date" value={form.date} onChange={(e) => set("date", e.target.value)} className={fieldClass} data-testid="f-date" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="mb-1 block text-sm font-medium md:text-xs">Start</Label>
              <Input type="time" value={form.start} onChange={(e) => set("start", e.target.value)} className={fieldClass} data-testid="f-start" />
            </div>
            <div>
              <Label className="mb-1 block text-sm font-medium md:text-xs">End</Label>
              <Input type="time" value={form.end} onChange={(e) => set("end", e.target.value)} className={fieldClass} data-testid="f-end" />
            </div>
          </div>
          <p className={`-mt-2 text-xs ${validTimes ? "text-muted-foreground" : "text-red-400"}`} data-testid="f-duration">
            {validTimes
              ? `${fmtH(duration)}${Number(form.rate) > 0 ? ` · ${fmtMoney(duration * Number(form.rate))}` : ""}`
              : "The end must be after the start (same day)."}
          </p>

          {conflict && (
            <div className="rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-400" data-testid="f-conflict">
              This person already works {clock(new Date(conflict.start_time))} – {clock(new Date(conflict.end_time))} that day ({conflict.title}).
            </div>
          )}

          <div>
            <Label className="mb-1 block text-sm font-medium md:text-xs">Role or task</Label>
            <Input
              value={form.title}
              onChange={(e) => set("title", e.target.value)}
              list="roster-titles"
              placeholder="e.g. Labourer, Waterproofing"
              className={fieldClass}
              data-testid="f-title"
            />
            <datalist id="roster-titles">
              {titles.map((t) => (
                <option key={t} value={t} />
              ))}
            </datalist>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="mb-1 block text-sm font-medium md:text-xs">Jobsite</Label>
              <select value={form.siteId} onChange={(e) => set("siteId", e.target.value)} className={selectClass} data-testid="f-site">
                <option value="">No jobsite</option>
                {sites.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name || s.title}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Label className="mb-1 block text-sm font-medium md:text-xs">Pay per hour ($)</Label>
              <Input
                type="number"
                inputMode="decimal"
                min="0"
                step="0.5"
                value={form.rate}
                onChange={(e) => setForm((f) => ({ ...f, rate: e.target.value, rateTouched: true }))}
                placeholder="0"
                className={fieldClass}
                data-testid="f-rate"
              />
            </div>
          </div>

          <div>
            <Label className="mb-1 block text-sm font-medium md:text-xs">Notes</Label>
            <textarea
              value={form.notes}
              onChange={(e) => set("notes", e.target.value)}
              rows={2}
              placeholder="Optional"
              className="w-full rounded-lg border border-border bg-background px-3 py-2 text-base text-foreground focus:outline-none focus:ring-1 focus:ring-primary md:rounded-sm md:text-sm"
              data-testid="f-notes"
            />
          </div>

          {editing && claims.length > 0 && (
            <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3" data-testid="claims-box">
              <div className="mb-1 text-xs font-semibold text-amber-500">Requests for this shift</div>
              {claims.map((a) => (
                <div key={a.id} className="flex items-center justify-between gap-2 py-1">
                  <span className="min-w-0 truncate text-sm">{a.user_name} wants this shift</span>
                  <div className="flex shrink-0 gap-2">
                    <Button size="sm" disabled={!!busy} onClick={() => review(a, "confirmed")} data-testid={`approve-${a.id}`}>
                      Approve
                    </Button>
                    <Button size="sm" variant="outline" disabled={!!busy} onClick={() => review(a, "declined")} data-testid={`decline-${a.id}`}>
                      Decline
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {editing && drops.length > 0 && (
            <div className="rounded-lg border border-red-500/40 bg-red-500/10 p-3" data-testid="drops-box">
              <div className="mb-1 text-xs font-semibold text-red-400">Wants to drop this shift</div>
              {drops.map((a) => (
                <div key={a.id} className="flex items-center justify-between gap-2 py-1">
                  <span className="min-w-0 truncate text-sm">{a.user_name}</span>
                  <div className="flex shrink-0 gap-2">
                    <Button size="sm" disabled={!!busy} onClick={() => review(a, "confirmed")} data-testid={`approve-${a.id}`}>
                      Let go
                    </Button>
                    <Button size="sm" variant="outline" disabled={!!busy} onClick={() => review(a, "declined")} data-testid={`decline-${a.id}`}>
                      Keep
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="mt-2 flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-between">
          {editing ? (
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                disabled={!!busy}
                onClick={remove}
                aria-label="Delete shift"
                className="h-12 flex-1 text-destructive md:h-9 md:flex-none"
                data-testid="f-delete"
              >
                <Trash2 className="mr-1.5 h-4 w-4" /> Delete
              </Button>
              {form.userId ? (
                <Button type="button" variant="outline" disabled={!!busy} onClick={makeOpen} className="h-12 flex-1 md:h-9 md:flex-none" data-testid="f-make-open">
                  Make open
                </Button>
              ) : null}
            </div>
          ) : (
            <span />
          )}
          <Button
            type="button"
            onClick={save}
            disabled={!canSave}
            className="h-12 w-full rounded-lg bg-primary text-base hover:bg-primary/90 sm:w-auto md:h-9 md:rounded-sm md:text-sm"
            data-testid="f-save"
          >
            {busy === "save" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {editing ? "Save changes" : "Add shift"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ---------- pagina ---------- */

export default function AdminRoster() {
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date()));
  const [users, setUsers] = useState([]);
  const [sites, setSites] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [siteFilter, setSiteFilter] = useState("");
  const [query, setQuery] = useState("");
  const [sheet, setSheet] = useState(null);
  const [dragOver, setDragOver] = useState("");
  const [copying, setCopying] = useState("");
  const reqRef = useRef(0);
  const dragRef = useRef(null);

  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)), [weekStart]);
  const todayKey = dayKey(new Date());
  const siteName = useMemo(() => Object.fromEntries(sites.map((s) => [s.id, s.name || s.title || "Jobsite"])), [sites]);
  const siteNameOf = (s) => (siteFilter ? "" : siteName[s.jobsite_id] || "");

  const loadShifts = useCallback(async (ws) => {
    const id = ++reqRef.current;
    setRefreshing(true);
    try {
      const { data } = await api.get("/shifts", { params: rangeParams(ws) });
      if (id === reqRef.current) setShifts(Array.isArray(data) ? data : []);
    } catch (e) {
      if (id === reqRef.current) toast.error(formatApiError(e.response?.data?.detail, "Could not load the roster."));
    } finally {
      if (id === reqRef.current) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, []);

  useEffect(() => {
    api.get("/company/users").then(({ data }) => setUsers(Array.isArray(data) ? data : [])).catch(() => toast.error("Could not load the team."));
    api.get("/jobsites").then(({ data }) => setSites(Array.isArray(data) ? data : [])).catch(() => {});
  }, []);

  useEffect(() => {
    loadShifts(weekStart);
  }, [weekStart, loadShifts]);

  const activeUsers = useMemo(
    () =>
      users
        .filter((u) => (u.status || "approved") === "approved")
        .sort((a, b) => (a.name || "").localeCompare(b.name || "")),
    [users]
  );

  const weekShifts = useMemo(() => {
    const end = addDays(weekStart, 7);
    return shifts.filter((s) => {
      const sd = new Date(s.start_time);
      return sd >= weekStart && sd < end && (!siteFilter || s.jobsite_id === siteFilter);
    });
  }, [shifts, weekStart, siteFilter]);

  const grid = useMemo(() => {
    const cells = {}; // userId -> dayKey -> [shifts]
    const openByDay = {};
    const rowStats = {};
    const dayStats = {};
    const known = new Set(activeUsers.map((u) => u.id));
    const extra = {};
    let openCount = 0;
    let requestCount = 0;
    let totalHours = 0;
    let totalCost = 0;
    const sorted = [...weekShifts].sort((a, b) => new Date(a.start_time) - new Date(b.start_time));
    for (const s of sorted) {
      const k = dayKey(new Date(s.start_time));
      const { assigned, claims, drops } = assignmentOf(s);
      requestCount += claims.length + drops.length;
      if (assigned.length === 0) {
        (openByDay[k] = openByDay[k] || []).push(s);
        openCount += 1;
        continue;
      }
      const h = shiftHours(s);
      const c = shiftCost(s);
      for (const a of assigned) {
        if (!known.has(a.user_id)) extra[a.user_id] = { id: a.user_id, name: a.user_name || "Former member", inactive: true };
        ((cells[a.user_id] = cells[a.user_id] || {})[k] = cells[a.user_id][k] || []).push(s);
        const rs = (rowStats[a.user_id] = rowStats[a.user_id] || { h: 0, c: 0 });
        rs.h += h;
        rs.c += c;
        const ds = (dayStats[k] = dayStats[k] || { h: 0, c: 0 });
        ds.h += h;
        ds.c += c;
        totalHours += h;
        totalCost += c;
      }
    }
    return { cells, openByDay, rowStats, dayStats, extra: Object.values(extra), openCount, requestCount, totalHours, totalCost };
  }, [weekShifts, activeUsers]);

  const q = query.trim().toLowerCase();
  const rows = useMemo(() => {
    const list = [...activeUsers, ...grid.extra];
    return q ? list.filter((u) => (u.name || "").toLowerCase().includes(q)) : list;
  }, [activeUsers, grid.extra, q]);

  const titles = useMemo(() => [...new Set(["Shift", ...shifts.map((s) => s.title).filter(Boolean)])], [shifts]);

  const go = (n) => setWeekStart((w) => addDays(w, 7 * n));
  const rangeLabel = `${shortDate(days[0])} – ${shortDate(days[6])} ${days[6].getFullYear()}`;

  /* --- abrir janelas --- */
  const openNew = (userId, day) => setSheet({ userId: userId || "", day });
  const openEdit = (shift) => setSheet({ shift });
  const afterChange = () => {
    setSheet(null);
    loadShifts(weekStart);
  };

  /* --- arrastar e soltar (computador) --- */
  const onDragStart = (ev, shift) => {
    dragRef.current = shift.id;
    try {
      ev.dataTransfer.setData("text/plain", shift.id);
      ev.dataTransfer.effectAllowed = "move";
    } catch (e) {
      // alguns navegadores nao deixam: usamos a referencia
    }
  };
  const onDragEnd = () => {
    dragRef.current = null;
    setDragOver("");
  };

  // userId: id da pessoa, ou null para deixar o turno aberto. day: o dia de destino.
  const moveShift = async (shiftId, userId, day) => {
    const shift = shifts.find((s) => s.id === shiftId);
    if (!shift) return;
    const sd = new Date(shift.start_time);
    const ed = new Date(shift.end_time);
    const current = assignmentOf(shift).assigned[0]?.user_id || null;
    const sameDay = dayKey(day) === dayKey(sd);
    if (userId === current && sameDay) return;

    const body = {};
    if (!sameDay) {
      const ns = new Date(day.getFullYear(), day.getMonth(), day.getDate(), sd.getHours(), sd.getMinutes());
      body.start_time = ns.toISOString();
      body.end_time = new Date(ns.getTime() + (ed - sd)).toISOString();
    }
    if (userId === null) body.unassign = true;
    else if (userId !== current) body.user_id = userId;

    // mostra a mudanca na hora; se o servidor recusar, volta como estava
    const target = users.find((u) => u.id === userId);
    const keepRequests = (shift.allocations || []).filter((a) => a.status === "pending_approval" && a.request_type !== "drop" && a.user_id !== userId);
    setShifts((prev) =>
      prev.map((s) =>
        s.id !== shiftId
          ? s
          : {
              ...s,
              ...(body.start_time ? { start_time: body.start_time, end_time: body.end_time } : {}),
              status: userId ? "allocated" : "open",
              allocations: userId
                ? [{ id: `tmp-${shiftId}`, user_id: userId, user_name: target?.name || "", status: "confirmed", request_type: "assign" }, ...keepRequests]
                : keepRequests,
            }
      )
    );
    try {
      const { data } = await api.post(`/shifts/${shiftId}/move`, body);
      setShifts((prev) => prev.map((s) => (s.id === shiftId ? data : s)));
    } catch (e) {
      setShifts((prev) => prev.map((s) => (s.id === shiftId ? shift : s)));
      toast.error(formatApiError(e.response?.data?.detail, "Could not move the shift."));
    }
  };

  const dropOn = (ev, userId, day) => {
    const id = dragRef.current || (() => {
      try {
        return ev.dataTransfer.getData("text/plain");
      } catch (e) {
        return "";
      }
    })();
    dragRef.current = null;
    if (id) moveShift(id, userId, day);
  };

  /* --- copiar a semana passada --- */
  const copyLastWeek = async () => {
    if (copying) return;
    try {
      const prevStart = addDays(weekStart, -7);
      const { data } = await api.get("/shifts", { params: rangeParams(prevStart) });
      const list = (Array.isArray(data) ? data : [])
        .filter((s) => {
          const sd = new Date(s.start_time);
          return sd >= prevStart && sd < weekStart && (!siteFilter || s.jobsite_id === siteFilter);
        })
        .sort((a, b) => new Date(a.start_time) - new Date(b.start_time));
      if (list.length === 0) {
        toast.info("There are no shifts last week to copy.");
        return;
      }
      const existing = weekShifts.length;
      const text =
        `Copy ${list.length} shift${list.length === 1 ? "" : "s"} from last week into this week?` +
        (existing ? `\n\nThis week already has ${existing}. They stay, and these are added.` : "");
      if (!confirm(text)) return;
      let done = 0;
      let skipped = 0;
      for (let i = 0; i < list.length; i += 1) {
        const s = list[i];
        setCopying(`${i + 1}/${list.length}`);
        const { data: created } = await api.post("/shifts", {
          title: s.title,
          start_time: addDays(new Date(s.start_time), 7).toISOString(),
          end_time: addDays(new Date(s.end_time), 7).toISOString(),
          role_required: s.role_required || "worker",
          hourly_rate: Number(s.hourly_rate) || 0,
          jobsite_id: s.jobsite_id || null,
          notes: s.notes || "",
        });
        const who = assignmentOf(s).assigned[0]?.user_id;
        if (who) {
          try {
            await api.post(`/shifts/${created.id}/allocate`, { user_id: who });
          } catch (e) {
            await api.delete(`/shifts/${created.id}`).catch(() => {});
            if (e.response?.status === 409) {
              skipped += 1;
              continue;
            }
            throw e;
          }
        }
        done += 1;
      }
      toast.success(`Copied ${done} shift${done === 1 ? "" : "s"}${skipped ? `, skipped ${skipped} (the person was already busy)` : ""}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, "Could not copy last week."));
    } finally {
      setCopying("");
      loadShifts(weekStart);
    }
  };

  const gridCols = "grid-cols-[128px_repeat(7,minmax(118px,1fr))] md:grid-cols-[190px_repeat(7,minmax(128px,1fr))]";

  return (
    <div className="mx-auto max-w-[1500px] p-4 md:p-10">
      <PageHeader
        title="Roster & Shifts"
        subtitle="Plan who works where. Tap an empty day to add a shift, tap a shift to change it, or drag it to another day or person."
        action={
          <Button
            onClick={() => openNew("", days.find((d) => dayKey(d) === todayKey) || days[0])}
            className="rounded-sm bg-primary hover:bg-primary/90"
            data-testid="add-shift-top"
          >
            <Plus className="mr-1.5 h-4 w-4" /> Add shift
          </Button>
        }
      />

      {/* barra de cima */}
      <div className="mb-3 flex flex-wrap items-center gap-2 md:gap-3">
        <div className="flex items-center gap-1">
          <Button variant="outline" onClick={() => go(-1)} aria-label="Previous week" className="h-11 w-11 p-0 md:h-9 md:w-9" data-testid="week-prev">
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <div className="min-w-[10.5rem] px-2 text-center text-sm font-semibold" data-testid="roster-range">
            {rangeLabel}
          </div>
          <Button variant="outline" onClick={() => go(1)} aria-label="Next week" className="h-11 w-11 p-0 md:h-9 md:w-9" data-testid="week-next">
            <ChevronRight className="h-4 w-4" />
          </Button>
          <Button variant="outline" onClick={() => setWeekStart(startOfWeek(new Date()))} className="ml-1 h-11 md:h-9" data-testid="week-today">
            Today
          </Button>
        </div>

        <select value={siteFilter} onChange={(e) => setSiteFilter(e.target.value)} className={`${selectClass} w-auto min-w-[10rem]`} aria-label="Filter by jobsite" data-testid="site-filter">
          <option value="">All jobsites</option>
          {sites.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name || s.title}
            </option>
          ))}
        </select>

        <div className="relative min-w-[10rem] flex-1 md:max-w-xs">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search people..." className={`${fieldClass} pl-9`} data-testid="people-search" />
        </div>

        <div className="ml-auto flex items-center gap-2">
          <Button variant="outline" onClick={copyLastWeek} disabled={!!copying} className="h-11 md:h-9" data-testid="copy-week">
            {copying ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Copy className="mr-1.5 h-4 w-4" />}
            {copying ? `Copying ${copying}` : "Copy last week"}
          </Button>
          <Button variant="outline" onClick={() => loadShifts(weekStart)} aria-label="Refresh" className="h-11 w-11 p-0 md:h-9 md:w-9" data-testid="refresh">
            <RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </div>

      <div className="mb-4 flex flex-wrap gap-2" data-testid="roster-stats">
        <Chip>{fmtH(grid.totalHours)} scheduled</Chip>
        <Chip>{fmtMoney(grid.totalCost)} labour</Chip>
        {grid.openCount > 0 && <Chip tone="warn">{grid.openCount} open shift{grid.openCount === 1 ? "" : "s"}</Chip>}
        {grid.requestCount > 0 && <Chip tone="warn">{grid.requestCount} request{grid.requestCount === 1 ? "" : "s"} to review</Chip>}
      </div>

      {loading ? (
        <div className="grid h-48 place-items-center text-sm text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-border bg-card md:rounded-sm" data-testid="roster-grid">
          <div className={`grid min-w-[980px] ${gridCols}`}>
            {/* dias */}
            <div className="sticky left-0 z-20 border-b border-border bg-card" />
            {days.map((d) => {
              const k = dayKey(d);
              return (
                <div key={k} className={`border-b border-l border-border p-2.5 text-center ${k === todayKey ? "bg-primary/[0.06]" : ""}`} data-testid={`day-${k}`}>
                  <div className={`text-sm font-bold ${k === todayKey ? "text-primary" : ""}`}>{weekdayName(d)}</div>
                  <div className="text-xs text-muted-foreground">{shortDate(d)}</div>
                </div>
              );
            })}

            {/* turnos abertos */}
            <div className="sticky left-0 z-10 flex flex-col justify-center border-b border-border bg-card p-3" data-testid="open-row-label">
              <div className="text-sm font-semibold">Open shifts</div>
              <div className="text-xs text-muted-foreground">{grid.openCount ? `${grid.openCount} to fill` : "None"}</div>
            </div>
            {days.map((d) => {
              const k = dayKey(d);
              return (
                <Cell
                  key={`open|${k}`}
                  id={`open|${k}`}
                  items={grid.openByDay[k] || []}
                  today={k === todayKey}
                  label={`Add an open shift on ${weekdayName(d)} ${shortDate(d)}`}
                  siteNameOf={siteNameOf}
                  dragOver={dragOver}
                  setDragOver={setDragOver}
                  onAdd={() => openNew("", d)}
                  onOpen={openEdit}
                  onDragStart={onDragStart}
                  onDragEnd={onDragEnd}
                  onDropHere={(ev) => dropOn(ev, null, d)}
                />
              );
            })}

            {/* pessoas */}
            {rows.map((u) => {
              const st = grid.rowStats[u.id];
              return (
                <Fragment key={u.id}>
                  <div className="sticky left-0 z-10 flex items-center gap-2.5 border-b border-border bg-card p-2.5 md:p-3" data-testid={`person-${u.id}`}>
                    <Avatar user={u} size={36} />
                    <div className="min-w-0">
                      <div className="truncate text-sm font-semibold leading-tight">{u.name}</div>
                      <div className="truncate text-xs text-muted-foreground" data-testid={`person-stats-${u.id}`}>
                        {u.inactive ? "Not active" : st ? `${fmtH(st.h)} · ${fmtMoney(st.c)}` : "No shifts"}
                      </div>
                    </div>
                  </div>
                  {days.map((d) => {
                    const k = dayKey(d);
                    return (
                      <Cell
                        key={`${u.id}|${k}`}
                        id={`${u.id}|${k}`}
                        items={grid.cells[u.id]?.[k] || []}
                        today={k === todayKey}
                        label={`Add a shift for ${u.name} on ${weekdayName(d)} ${shortDate(d)}`}
                        siteNameOf={siteNameOf}
                        dragOver={dragOver}
                        setDragOver={setDragOver}
                        onAdd={() => openNew(u.id, d)}
                        onOpen={openEdit}
                        onDragStart={onDragStart}
                        onDragEnd={onDragEnd}
                        onDropHere={(ev) => dropOn(ev, u.id, d)}
                      />
                    );
                  })}
                </Fragment>
              );
            })}

            {rows.length === 0 && (
              <div className="col-span-8 border-b border-border p-8 text-center text-sm text-muted-foreground" data-testid="no-people">
                {q ? "No one matches your search." : "There are no approved people in your team yet."}
              </div>
            )}

            {/* totais */}
            <div className="sticky left-0 z-10 flex items-center bg-card p-3 text-sm font-semibold">Total</div>
            {days.map((d) => {
              const k = dayKey(d);
              const ds = grid.dayStats[k];
              return (
                <div key={k} className="border-l border-border p-2.5 text-center text-xs text-muted-foreground" data-testid={`total-${k}`}>
                  {ds ? (
                    <>
                      <div className="font-semibold text-foreground">{fmtH(ds.h)}</div>
                      <div>{fmtMoney(ds.c)}</div>
                    </>
                  ) : (
                    "—"
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {sheet && (
        <ShiftSheet
          key={sheet.shift ? sheet.shift.id : `new-${sheet.userId}-${dayKey(sheet.day)}`}
          state={sheet}
          users={activeUsers}
          sites={sites}
          shifts={shifts}
          titles={titles}
          siteFilter={siteFilter}
          onClose={() => setSheet(null)}
          onChanged={afterChange}
        />
      )}
    </div>
  );
}
